// frontend/src/pages/SchedulerPage.js - VERSIONE CON NOTIFICHE EMAIL E FILTRI

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { 
  Clock, Plus, Edit, Trash2, Play, Pause, RefreshCw, 
  Zap, CheckCircle, XCircle,
  Mail, Filter, MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';
import CPVTreeSelector from '../components/CPVTreeSelector';
import SOASelector from '../components/SOASelector';
import { shouldSuppressRateLimit } from '../utils/httpErrors';
import { COLORS } from '../config/theme';

// Dati regioni e province italiane
const REGIONI_ITALIANE = [
  'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
  'Friuli-Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
  'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
  'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto'
];

const PROVINCE_PER_REGIONE = {
  'Abruzzo': ["L'Aquila", 'Chieti', 'Pescara', 'Teramo'],
  'Basilicata': ['Matera', 'Potenza'],
  'Calabria': ['Catanzaro', 'Cosenza', 'Crotone', 'Reggio Calabria', 'Vibo Valentia'],
  'Campania': ['Avellino', 'Benevento', 'Caserta', 'Napoli', 'Salerno'],
  'Emilia-Romagna': ['Bologna', 'Ferrara', 'Forlì-Cesena', 'Modena', 'Parma', 'Piacenza', 'Ravenna', 'Reggio Emilia', 'Rimini'],
  'Friuli-Venezia Giulia': ['Gorizia', 'Pordenone', 'Trieste', 'Udine'],
  'Lazio': ['Frosinone', 'Latina', 'Rieti', 'Roma', 'Viterbo'],
  'Liguria': ['Genova', 'Imperia', 'La Spezia', 'Savona'],
  'Lombardia': ['Bergamo', 'Brescia', 'Como', 'Cremona', 'Lecco', 'Lodi', 'Mantova', 'Milano', 'Monza e Brianza', 'Pavia', 'Sondrio', 'Varese'],
  'Marche': ['Ancona', 'Ascoli Piceno', 'Fermo', 'Macerata', 'Pesaro e Urbino'],
  'Molise': ['Campobasso', 'Isernia'],
  'Piemonte': ['Alessandria', 'Asti', 'Biella', 'Cuneo', 'Novara', 'Torino', 'Verbano-Cusio-Ossola', 'Vercelli'],
  'Puglia': ['Bari', 'Barletta-Andria-Trani', 'Brindisi', 'Foggia', 'Lecce', 'Taranto'],
  'Sardegna': ['Cagliari', 'Nuoro', 'Oristano', 'Sassari', 'Sud Sardegna'],
  'Sicilia': ['Agrigento', 'Caltanissetta', 'Catania', 'Enna', 'Messina', 'Palermo', 'Ragusa', 'Siracusa', 'Trapani'],
  'Toscana': ['Arezzo', 'Firenze', 'Grosseto', 'Livorno', 'Lucca', 'Massa-Carrara', 'Pisa', 'Pistoia', 'Prato', 'Siena'],
  'Trentino-Alto Adige': ['Bolzano', 'Trento'],
  'Umbria': ['Perugia', 'Terni'],
  "Valle d'Aosta": ['Aosta'],
  'Veneto': ['Belluno', 'Padova', 'Rovigo', 'Treviso', 'Venezia', 'Verona', 'Vicenza']
};

const SchedulerPage = () => {
  const { user } = useAuth();
  const { isAdmin, isGuest } = usePermissions();
  const isAdminUser = isAdmin();
  const isGuestUser = isGuest();
  const [autoUpdateConfigs, setAutoUpdateConfigs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [quota, setQuota] = useState(null); // { limit_per_day, used_today, remaining_today, max_active_schedules, active_schedules }
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [runningJob, setRunningJob] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    schedule_type: 'daily',
    hour: 8,
    minute: 0,
    day_of_week: '1',
    custom_cron: '',
    is_active: true,
    // 🔥 NUOVO: Filtri
    filters: {
      search_text: '',
      regioni: [],
      province: [],
      cpv_codes: [],
      soa_codes: []
    }
  });

  // Province disponibili in base alle regioni selezionate
  const availableProvince = useMemo(() => {
    const provinces = [];
    formData.filters.regioni.forEach(regione => {
      if (PROVINCE_PER_REGIONE[regione]) {
        provinces.push(...PROVINCE_PER_REGIONE[regione]);
      }
    });
    return provinces.sort();
  }, [formData.filters.regioni]);

  // Carica configurazioni all'avvio
  useEffect(() => {
    if (isGuestUser) {
      // Guest: deve vedere solo la creazione (modal bloccato), senza chiamate e senza liste.
      setAutoUpdateConfigs([]);
      setActiveJobs([]);
      setQuota(null);
      setIsLoading(false);
      return;
    }

    loadConfigs();
    loadActiveJobs();
    loadQuota();
  }, [isGuestUser]);

  const loadConfigs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/scheduler/auto-update`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoUpdateConfigs(response.data);
    } catch (error) {
      console.error('Errore caricamento configurazioni:', error);
      toast.error('Errore nel caricamento delle configurazioni');
    } finally {
      setIsLoading(false);
    }
  };

  const loadActiveJobs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/scheduler/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveJobs(response.data);
    } catch (error) {
      console.error('Errore caricamento jobs:', error);
    }
  };

  const loadQuota = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/scheduler/auto-update/quota`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuota(response.data);
    } catch (error) {
      // Non blocchiamo la pagina se quota fallisce
      console.error('Errore caricamento quota:', error);
    }
  };

  const activeNotificationCount = useMemo(() => {
    return (autoUpdateConfigs || []).filter(c => c && c.is_active).length;
  }, [autoUpdateConfigs]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
    }));
  };

  // Handler per i filtri
  const handleRegioniChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        regioni: selected,
        // Reset province se la regione non è più selezionata
        province: prev.filters.province.filter(p => 
          selected.some(r => PROVINCE_PER_REGIONE[r]?.includes(p))
        )
      }
    }));
  };

  const handleProvinceChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        province: selected
      }
    }));
  };

  const handleSelectAllRegions = () => {
    const allRegions = [...REGIONI_ITALIANE];
    const allProvinces = Array.from(
      new Set(
        allRegions.flatMap((r) => PROVINCE_PER_REGIONE[r] || [])
      )
    ).sort();

    setFormData((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        regioni: allRegions,
        province: allProvinces,
      },
    }));
  };

  const handleClearRegionsAndProvinces = () => {
    setFormData((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        regioni: [],
        province: [],
      },
    }));
  };

  const handleSelectAllProvincesForSelectedRegions = () => {
    // Seleziona tutte le province disponibili per le regioni attualmente selezionate
    setFormData((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        province: [...availableProvince],
      },
    }));
  };

  const handleClearProvinces = () => {
    setFormData((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        province: [],
      },
    }));
  };

  const handleCPVCodesChange = (codes) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        cpv_codes: codes
      }
    }));
  };

  const handleSOACodesChange = (codes) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        soa_codes: codes
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isGuestUser) {
      toast.error('Funzione disponibile solo per utenti Associati.');
      return;
    }

    // ✅ Limite schedulazioni: max 3 notifiche attive
    const turningOn =
      Boolean(formData.is_active) &&
      (
        !editingConfig ||
        (editingConfig && !editingConfig.is_active)
      );
    if (turningOn && activeNotificationCount >= 3) {
      toast.error('Limite raggiunto: massimo 3 notifiche attive.');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      
      // Prepara i dati da inviare
      const submitData = {
        name: formData.name,
        schedule_type: formData.schedule_type,
        hour: formData.hour,
        minute: formData.minute,
        day_of_week: formData.day_of_week,
        custom_cron: formData.custom_cron,
        is_active: formData.is_active,
        filters: formData.filters
      };
      
      if (editingConfig) {
        // Aggiorna configurazione esistente
        await axios.put(
          `${API_BASE_URL}/scheduler/auto-update/${editingConfig.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('✅ Configurazione aggiornata!');
      } else {
        // Crea nuova configurazione
        await axios.post(
          `${API_BASE_URL}/scheduler/auto-update`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('✅ Notifica creata!');
      }

      // Ricarica dati
      await loadConfigs();
      await loadActiveJobs();
      await loadQuota();
      
      // Reset form
      resetForm();
      
    } catch (error) {
      console.error('Errore salvataggio:', error);
      if (shouldSuppressRateLimit(error)) {
        return;
      }
      const errorMsg = error.response?.data?.detail || 'Errore nel salvataggio';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      schedule_type: 'daily',
      hour: 8,
      minute: 0,
      day_of_week: '1',
      custom_cron: '',
      is_active: true,
      filters: {
        search_text: '',
        regioni: [],
        province: [],
        cpv_codes: [],
        soa_codes: []
      }
    });
    setShowForm(false);
    setEditingConfig(null);
  };

  const handleEdit = (config) => {
    if (isGuestUser) {
      toast.error('Funzione disponibile solo per utenti Associati.');
      return;
    }
    // Estrai dati dalla configurazione
    const cronParts = config.cron_expression.split(' ');
    const filters = config.filters || { search_text: '', regioni: [], province: [], cpv_codes: [], soa_codes: [] };
    
    setFormData({
      name: config.name,
      schedule_type: config.schedule_type || 'custom',
      hour: parseInt(cronParts[1]) || 8,
      minute: parseInt(cronParts[0]) || 0,
      day_of_week: cronParts[4] !== '*' ? cronParts[4] : '1',
      custom_cron: config.cron_expression,
      is_active: config.is_active,
      filters: {
        search_text: filters.search_text || '',
        regioni: filters.regioni || [],
        province: filters.province || [],
        cpv_codes: filters.cpv_codes || [],
        soa_codes: filters.soa_codes || []
      }
    });
    setEditingConfig(config);
    setShowForm(true);
  };

  const handleDelete = async (configId) => {
    if (isGuestUser) {
      toast.error('Funzione disponibile solo per utenti Associati.');
      return;
    }
    if (!window.confirm('Sei sicuro di voler eliminare questa notifica?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${API_BASE_URL}/scheduler/auto-update/${configId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('✅ Notifica eliminata');
      await loadConfigs();
      await loadActiveJobs();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleToggle = async (configId) => {
    if (isGuestUser) {
      toast.error('Funzione disponibile solo per utenti Associati.');
      return;
    }
    const cfg = (autoUpdateConfigs || []).find(c => c && c.id === configId);
    const willEnable = cfg ? !Boolean(cfg.is_active) : false;
    if (willEnable && activeNotificationCount >= 3) {
      toast.error('Limite raggiunto: massimo 3 notifiche attive.');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/scheduler/auto-update/${configId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newStatus = response.data.is_active ? 'attivata' : 'disattivata';
      toast.success(`✅ Notifica ${newStatus}`);
      
      await loadConfigs();
      await loadActiveJobs();
      await loadQuota();
    } catch (error) {
      toast.error('Errore nel cambio stato');
    }
  };

  const handleRunNow = async (configId, configName) => {
    if (isGuestUser) {
      toast.error('Funzione disponibile solo per utenti Associati.');
      return;
    }
    if (quota && typeof quota.remaining_today === 'number' && quota.remaining_today <= 0) {
      toast.error('Invio email massimo 3 al giorno');
      return;
    }
    try {
      setRunningJob(configId);
      const token = localStorage.getItem('access_token');
      
      await axios.post(
        `${API_BASE_URL}/scheduler/auto-update/${configId}/run-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`🚀 "${configName}" avviato! Riceverai un'email con i risultati.`);
      // Best-effort: aggiorna quota localmente (backend applica hard-limit comunque)
      setQuota((prev) => {
        if (!prev || typeof prev.used_today !== 'number' || typeof prev.remaining_today !== 'number') return prev;
        return {
          ...prev,
          used_today: prev.used_today + 1,
          remaining_today: Math.max(0, prev.remaining_today - 1),
        };
      });
      
    } catch (error) {
      if (shouldSuppressRateLimit(error)) {
        await loadQuota();
        return;
      }
      const status = error?.response?.status;
      if (status === 429) {
        toast.error('Invio email massimo 3 al giorno');
      } else {
        const msg = error?.response?.data?.detail || 'Errore nell\'avvio';
        toast.error(msg);
      }
      await loadQuota();
    } finally {
      setRunningJob(null);
    }
  };

  const getScheduleDescription = (config) => {
    const type = config.schedule_type;
    const cron = config.cron_expression;
    const parts = cron.split(' ');
    
    const hour = parts[1].padStart(2, '0');
    const minute = parts[0].padStart(2, '0');
    
    if (type === 'daily') {
      return `Ogni giorno alle ${hour}:${minute}`;
    } else if (type === 'weekly') {
      const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
      const dayNum = parseInt(parts[4]) || 0;
      return `Ogni ${days[dayNum]} alle ${hour}:${minute}`;
    }
    return `Cron: ${cron}`;
  };

  const formatNextRun = (nextRun) => {
    if (!nextRun) return 'N/D';
    const date = new Date(nextRun);
    return date.toLocaleString('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFiltersSummary = (filters) => {
    if (!filters) return 'Nessun filtro';
    
    const parts = [];
    if (filters.regioni?.length > 0) {
      parts.push(`${filters.regioni.length} regioni`);
    }
    if (filters.province?.length > 0) {
      parts.push(`${filters.province.length} province`);
    }
    if (filters.cpv_codes?.length > 0) {
      parts.push(`${filters.cpv_codes.length} CPV`);
    }
    if (filters.soa_codes?.length > 0) {
      parts.push(`${filters.soa_codes.length} SOA`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Tutte le gare';
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 
              className="text-4xl font-bold mb-4 flex items-center gap-3" 
              style={{ color: COLORS.text }}
            >
              <Mail className="h-10 w-10" style={{ color: COLORS.primary }} />
              Notifiche Gare
            </h1>
            <p className="text-lg" style={{ color: COLORS.text2 }}>
              Ricevi email automatiche con le gare corrispondenti ai tuoi filtri
            </p>
          </div>
          <div className="flex gap-3">
            {!isGuestUser && (
              <button
                onClick={() => { loadConfigs(); loadActiveJobs(); }}
                className={[
                  "inline-flex items-center px-4 py-2 rounded-lg transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                ].join(' ')}
                style={{ 
                  color: COLORS.text, 
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Ricarica
              </button>
            )}
            <button
              onClick={() => setShowForm(true)}
              disabled={!isGuestUser && activeNotificationCount >= 3}
              className={[
                "inline-flex items-center px-4 py-2 rounded-lg transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              ].join(' ')}
              style={{ 
                backgroundColor: COLORS.primary,
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.primaryHover)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.primary)}
            >
              <Plus className="h-5 w-5 mr-2" />
              Nuova Notifica
            </button>
          </div>
        </div>
      </div>
        
        {/* Info Box */}
        <div 
          className="rounded-xl p-4 mb-8 border"
          style={{ 
            backgroundColor: COLORS.softBlue,
            borderColor: COLORS.primary
          }}
        >
          <div className="flex items-start">
            <Mail className="h-5 w-5 mt-0.5 mr-3" style={{ color: COLORS.primary }} />
            <div>
              <h3 className="text-sm font-semibold mb-1" style={{ color: COLORS.text }}>
                Come funziona?
              </h3>
              <p className="text-sm mt-1" style={{ color: COLORS.text2 }}>
                Configura i filtri (regione, provincia, categorie CPV/SOA) e scegli quando ricevere le notifiche.
                Il sistema cercherà automaticamente le nuove gare corrispondenti ai tuoi criteri 
                e ti invierà un'email con i risultati. La notifica rimarrà attiva finché non la disattiverai.
              </p>
              {!isGuestUser && (
                <p className="text-sm mt-2" style={{ color: COLORS.text2 }}>
                  <strong>Email massime giornaliere:</strong> 3
                </p>
              )}
              {!isGuestUser && quota && typeof quota.limit_per_day === 'number' && (
                <p className="text-sm mt-2" style={{ color: COLORS.text2 }}>
                  <strong>Quota giornaliera:</strong> {quota.used_today ?? 0}/{quota.limit_per_day} email notifiche inviate oggi.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
            onClick={(e) => {
              // Chiudi il modale se si clicca sullo sfondo
              if (e.target === e.currentTarget) {
                resetForm();
              }
            }}
          >
            <div 
              className="rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto" 
              style={{ 
                backgroundColor: '#FFFFFF',
                border: `1px solid ${COLORS.border}`
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 
                  className="text-2xl font-bold mb-6"
                  style={{ color: COLORS.text }}
                >
                  {editingConfig ? '✏️ Modifica Notifica' : '➕ Nuova Notifica'}
                </h2>
                
                <div className="relative">
                  {/* Overlay guest: blocca interazione mantenendo visibile il contenuto */}
                  {isGuestUser && (
                    <div className="absolute inset-0 z-10 rounded-xl bg-gray-500/20 border border-gray-300 pointer-events-auto">
                      <div className="p-4">
                        <div className="rounded-lg bg-white/70 border border-gray-300 px-4 py-3 text-sm text-gray-800">
                          Accesso in modalità <strong>Guest</strong>: puoi visualizzare i filtri, ma non puoi creare o modificare notifiche.
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <fieldset disabled={isGuestUser} className={isGuestUser ? "select-none" : ""}>
                  {/* Nome */}
                  <div>
                    <label 
                      className="block text-sm font-semibold mb-2"
                      style={{ color: COLORS.text }}
                    >
                      Nome Notifica *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={[
                        "w-full px-3 py-2 rounded-lg border text-sm",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      ].join(' ')}
                      style={{
                        borderColor: COLORS.border,
                        color: COLORS.text,
                      }}
                      placeholder="es. Gare Campania - Lavori Edili"
                    />
                  </div>

                  {/* Sezione Filtri */}
                  <div className="border-t pt-6" style={{ borderColor: COLORS.border }}>
                    <h3 
                      className="text-lg font-semibold mb-4 flex items-center"
                      style={{ color: COLORS.text }}
                    >
                      <Filter className="h-5 w-5 mr-2" style={{ color: COLORS.primary }} />
                      Filtri di Ricerca
                    </h3>

                    {/* Filtro Testo */}
                    <div className="mb-4">
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: COLORS.text }}
                      >
                        Testo (match su titolo o descrizione)
                      </label>
                      <input
                        type="text"
                        value={formData.filters.search_text}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            filters: {
                              ...prev.filters,
                              search_text: e.target.value,
                            },
                          }))
                        }
                        className={[
                          "w-full px-3 py-2 rounded-lg border text-sm",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                        ].join(" ")}
                        style={{
                          borderColor: COLORS.border,
                          color: COLORS.text,
                        }}
                        placeholder="es. manutenzione, impianti, lavori edili..."
                      />
                    </div>
                    
                    {/* Regioni e Province */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label 
                          className="block text-sm font-semibold mb-2 flex items-center"
                          style={{ color: COLORS.text }}
                        >
                          <MapPin className="h-4 w-4 mr-1" style={{ color: COLORS.success }} />
                          Regioni
                        </label>
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={handleSelectAllRegions}
                            className="px-3 py-1.5 rounded-md text-xs border"
                            style={{ borderColor: COLORS.border, color: COLORS.text }}
                          >
                            Tutte le regioni
                          </button>
                          <button
                            type="button"
                            onClick={handleClearRegionsAndProvinces}
                            className="px-3 py-1.5 rounded-md text-xs border"
                            style={{ borderColor: COLORS.border, color: COLORS.text }}
                          >
                            Pulisci
                          </button>
                        </div>
                        <select
                          multiple
                          value={formData.filters.regioni}
                          onChange={handleRegioniChange}
                          className={[
                            "w-full px-3 py-2 rounded-lg border text-sm",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                          ].join(' ')}
                          style={{
                            borderColor: COLORS.border,
                            color: COLORS.text,
                          }}
                          size={5}
                        >
                          {REGIONI_ITALIANE.map(regione => (
                            <option key={regione} value={regione}>{regione}</option>
                          ))}
                        </select>
                        <p className="text-xs mt-1" style={{ color: COLORS.text2 }}>
                          Ctrl+Click per selezione multipla
                        </p>
                      </div>
                      
                      <div>
                        <label 
                          className="block text-sm font-semibold mb-2 flex items-center"
                          style={{ color: COLORS.text }}
                        >
                          <MapPin className="h-4 w-4 mr-1" style={{ color: COLORS.primary }} />
                          Province
                        </label>
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={handleSelectAllProvincesForSelectedRegions}
                            disabled={availableProvince.length === 0}
                            className={[
                              "px-3 py-1.5 rounded-md text-xs border",
                              availableProvince.length === 0 ? "opacity-50 cursor-not-allowed" : "",
                            ].join(" ")}
                            style={{ borderColor: COLORS.border, color: COLORS.text }}
                          >
                            Tutte le province
                          </button>
                          <button
                            type="button"
                            onClick={handleClearProvinces}
                            disabled={formData.filters.province.length === 0}
                            className={[
                              "px-3 py-1.5 rounded-md text-xs border",
                              formData.filters.province.length === 0 ? "opacity-50 cursor-not-allowed" : "",
                            ].join(" ")}
                            style={{ borderColor: COLORS.border, color: COLORS.text }}
                          >
                            Pulisci
                          </button>
                        </div>
                        <select
                          multiple
                          value={formData.filters.province}
                          onChange={handleProvinceChange}
                          className={[
                            "w-full px-3 py-2 rounded-lg border text-sm",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                            "disabled:bg-gray-50 disabled:text-gray-400",
                          ].join(' ')}
                          style={{
                            borderColor: COLORS.border,
                            color: COLORS.text,
                          }}
                          size={5}
                          disabled={availableProvince.length === 0}
                        >
                          {availableProvince.map(provincia => (
                            <option key={provincia} value={provincia}>{provincia}</option>
                          ))}
                        </select>
                        <p className="text-xs mt-1" style={{ color: COLORS.text2 }}>
                          {availableProvince.length === 0 
                            ? 'Seleziona prima una regione' 
                            : 'Ctrl+Click per selezione multipla'}
                        </p>
                      </div>
                    </div>

                    {/* Filtro CPV */}
                    <div className="mb-4">
                      <CPVTreeSelector
                        selectedCodes={formData.filters.cpv_codes}
                        onCodesChange={handleCPVCodesChange}
                        maxHeight="300px"
                        forceExpanded={isGuestUser}
                        disabled={isGuestUser}
                      />
                    </div>

                    {/* Filtro SOA */}
                    <div className="mb-4">
                      <SOASelector
                        selectedSOACodes={formData.filters.soa_codes}
                        onSOACodesChange={handleSOACodesChange}
                        maxHeight="300px"
                        forceExpanded={isGuestUser}
                        disabled={isGuestUser}
                      />
                    </div>
                  </div>

                  {/* Sezione Schedulazione */}
                  <div className="border-t pt-6" style={{ borderColor: COLORS.border }}>
                    <h3 
                      className="text-lg font-semibold mb-4 flex items-center"
                      style={{ color: COLORS.text }}
                    >
                      <Clock className="h-5 w-5 mr-2" style={{ color: COLORS.primary }} />
                      Frequenza Notifica
                    </h3>

                    {/* Tipo Schedulazione */}
                    <div className="mb-4">
                      <label 
                        className="block text-sm font-semibold mb-2"
                        style={{ color: COLORS.text }}
                      >
                        Frequenza *
                      </label>
                      <select
                        name="schedule_type"
                        value={formData.schedule_type}
                        onChange={handleInputChange}
                        className={[
                          "w-full px-3 py-2 rounded-lg border text-sm",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                        ].join(' ')}
                        style={{
                          borderColor: COLORS.border,
                          color: COLORS.text,
                        }}
                      >
                        <option value="daily">📅 Giornaliero</option>
                        <option value="weekly">📆 Settimanale</option>
                        <option value="custom">⚙️ Personalizzato (Cron)</option>
                      </select>
                    </div>

                    {/* Orario (per daily e weekly) */}
                    {(formData.schedule_type === 'daily' || formData.schedule_type === 'weekly') && (
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label 
                            className="block text-sm font-semibold mb-2"
                            style={{ color: COLORS.text }}
                          >
                            Ora *
                          </label>
                          <select
                            name="hour"
                            value={formData.hour}
                            onChange={handleInputChange}
                            className={[
                              "w-full px-3 py-2 rounded-lg border text-sm",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                            ].join(' ')}
                            style={{
                              borderColor: COLORS.border,
                              color: COLORS.text,
                            }}
                          >
                            {[...Array(24)].map((_, i) => (
                              <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label 
                            className="block text-sm font-semibold mb-2"
                            style={{ color: COLORS.text }}
                          >
                            Minuto *
                          </label>
                          <select
                            name="minute"
                            value={formData.minute}
                            onChange={handleInputChange}
                            className={[
                              "w-full px-3 py-2 rounded-lg border text-sm",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                            ].join(' ')}
                            style={{
                              borderColor: COLORS.border,
                              color: COLORS.text,
                            }}
                          >
                            {[0, 15, 30, 45].map(m => (
                              <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Giorno settimana (per weekly) */}
                    {formData.schedule_type === 'weekly' && (
                      <div className="mb-4">
                        <label 
                          className="block text-sm font-semibold mb-2"
                          style={{ color: COLORS.text }}
                        >
                          Giorno della Settimana *
                        </label>
                        <select
                          name="day_of_week"
                          value={formData.day_of_week}
                          onChange={handleInputChange}
                          className={[
                            "w-full px-3 py-2 rounded-lg border text-sm",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                          ].join(' ')}
                          style={{
                            borderColor: COLORS.border,
                            color: COLORS.text,
                          }}
                        >
                          <option value="1">Lunedì</option>
                          <option value="2">Martedì</option>
                          <option value="3">Mercoledì</option>
                          <option value="4">Giovedì</option>
                          <option value="5">Venerdì</option>
                          <option value="6">Sabato</option>
                          <option value="0">Domenica</option>
                        </select>
                      </div>
                    )}

                    {/* Cron personalizzato */}
                    {formData.schedule_type === 'custom' && (
                      <div className="mb-4">
                        <label 
                          className="block text-sm font-semibold mb-2"
                          style={{ color: COLORS.text }}
                        >
                          Espressione Cron *
                        </label>
                        <input
                          type="text"
                          name="custom_cron"
                          value={formData.custom_cron}
                          onChange={handleInputChange}
                          required
                          className={[
                            "w-full px-3 py-2 rounded-lg border text-sm",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                          ].join(' ')}
                          style={{
                            borderColor: COLORS.border,
                            color: COLORS.text,
                          }}
                          placeholder="0 8 * * *"
                        />
                        <p className="mt-1 text-xs" style={{ color: COLORS.text2 }}>
                          Formato: minuto ora giorno mese giorno_settimana (es: "0 8 * * *" = ogni giorno alle 8:00)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Attivo */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className={[
                        "h-4 w-4 rounded border",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      ].join(' ')}
                      style={{
                        borderColor: COLORS.border,
                        accentColor: COLORS.primary,
                      }}
                    />
                    <label className="ml-2 text-sm" style={{ color: COLORS.text }}>
                      Attiva immediatamente
                    </label>
                  </div>

                  {/* Pulsanti */}
                    </fieldset>

                  <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: COLORS.border }}>
                    <button
                      type="button"
                      onClick={resetForm}
                      className={[
                        "px-4 py-2 rounded-lg transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      ].join(' ')}
                      style={{ 
                        color: COLORS.text, 
                        border: `1px solid ${COLORS.border}`,
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading || isGuestUser}
                      className={[
                        "px-4 py-2 rounded-lg transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                        "disabled:opacity-50",
                      ].join(' ')}
                      style={{ 
                        backgroundColor: COLORS.primary,
                        color: '#FFFFFF'
                      }}
                      onMouseEnter={(e) => {
                        if (!isLoading && !isGuestUser) e.currentTarget.style.backgroundColor = COLORS.primaryHover;
                      }}
                      onMouseLeave={(e) => {
                        if (!isLoading) e.currentTarget.style.backgroundColor = COLORS.primary;
                      }}
                    >
                      {isLoading ? 'Salvando...' : (editingConfig ? 'Aggiorna' : 'Crea')}
                    </button>
                  </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista Configurazioni */}
        {!isGuestUser && (
        <div 
          className="rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.05)] overflow-hidden"
          style={{ 
            backgroundColor: '#FFFFFF',
            border: `1px solid ${COLORS.border}`
          }}
        >
          <div className="p-6">
            <h2 
              className="text-xl font-semibold mb-4"
              style={{ color: COLORS.text }}
            >
              📋 Le Mie Notifiche ({autoUpdateConfigs.length})
            </h2>
            
            {isLoading ? (
              <div className="text-center py-12">
                <div 
                  className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                  style={{ borderColor: COLORS.primary }}
                ></div>
                <p style={{ color: COLORS.text2 }}>Caricamento...</p>
              </div>
            ) : autoUpdateConfigs.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 mx-auto mb-4" style={{ color: COLORS.border }} />
                <h3 className="text-lg font-medium mb-2" style={{ color: COLORS.text }}>
                  Nessuna notifica configurata
                </h3>
                <p className="mb-4" style={{ color: COLORS.text2 }}>
                  Crea la tua prima notifica per ricevere email sulle gare di tuo interesse
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  disabled={!isGuestUser && activeNotificationCount >= 3}
                  className={[
                    "inline-flex items-center px-4 py-2 rounded-lg transition-colors",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  ].join(' ')}
                  style={{ 
                    backgroundColor: COLORS.primary,
                    color: '#FFFFFF'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.primaryHover)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.primary)}
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Crea Notifica
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {autoUpdateConfigs.map((config) => (
                  <div
                    key={config.id}
                    className="p-5 rounded-xl transition-all"
                    style={{
                      border: `1px solid ${config.is_active ? COLORS.success : COLORS.border}`,
                      backgroundColor: config.is_active ? '#D1FAE5' : COLORS.bg,
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-semibold" style={{ color: COLORS.text }}>
                            {config.name}
                          </h3>
                          <span
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: config.is_active ? '#D1FAE5' : COLORS.border,
                              color: config.is_active ? '#065F46' : COLORS.text2
                            }}
                          >
                            {config.is_active ? (
                              <><CheckCircle className="h-3 w-3 mr-1" /> Attivo</>
                            ) : (
                              <><XCircle className="h-3 w-3 mr-1" /> Inattivo</>
                            )}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span style={{ color: COLORS.text2 }}>📅 Frequenza:</span>
                            <p className="font-medium" style={{ color: COLORS.text }}>
                              {getScheduleDescription(config)}
                            </p>
                          </div>
                          <div>
                            <span style={{ color: COLORS.text2 }}>⏰ Prossima:</span>
                            <p className="font-medium" style={{ color: COLORS.text }}>
                              {config.is_active ? formatNextRun(config.next_run) : 'Disattivato'}
                            </p>
                          </div>
                          <div>
                            <span style={{ color: COLORS.text2 }}>🔍 Filtri:</span>
                            <p className="font-medium" style={{ color: COLORS.text }}>
                              {getFiltersSummary(config.filters)}
                            </p>
                          </div>
                          <div>
                            <span style={{ color: COLORS.text2 }}>📊 Ultima esec.:</span>
                            <p className="font-medium" style={{ color: COLORS.text }}>
                              {config.last_run 
                                ? `${config.last_run_results || 0} gare trovate`
                                : 'Mai eseguita'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center flex-wrap gap-2 ml-0 md:ml-4 md:justify-end">
                        {/* Esegui Ora */}
                        <button
                          onClick={() => handleRunNow(config.id, config.name)}
                          disabled={
                            runningJob === config.id ||
                            (!isGuestUser && quota && typeof quota.remaining_today === 'number' && quota.remaining_today <= 0)
                          }
                          className="p-1.5 md:p-2 rounded-lg transition-colors disabled:opacity-50"
                          style={{ 
                            color: COLORS.primary,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (runningJob !== config.id) e.currentTarget.style.backgroundColor = COLORS.softBlue;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title={
                            (!isGuestUser && quota && typeof quota.remaining_today === 'number' && quota.remaining_today <= 0)
                              ? 'Limite giornaliero raggiunto (3 email)'
                              : 'Esegui ora'
                          }
                        >
                          {runningJob === config.id ? (
                            <RefreshCw className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                          ) : (
                            <Zap className="h-4 w-4 md:h-5 md:w-5" />
                          )}
                        </button>
                        
                        {/* Toggle Attivo */}
                        <button
                          onClick={() => handleToggle(config.id)}
                          className="p-1.5 md:p-2 rounded-lg transition-colors"
                          style={{ 
                            color: config.is_active ? COLORS.accent : COLORS.success,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = config.is_active ? COLORS.softYellow : '#D1FAE5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title={config.is_active ? 'Disattiva' : 'Attiva'}
                        >
                          {config.is_active ? (
                            <Pause className="h-4 w-4 md:h-5 md:w-5" />
                          ) : (
                            <Play className="h-4 w-4 md:h-5 md:w-5" />
                          )}
                        </button>
                        
                        {/* Modifica */}
                        <button
                          onClick={() => handleEdit(config)}
                          className="p-1.5 md:p-2 rounded-lg transition-colors"
                          style={{ 
                            color: COLORS.text2,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          title="Modifica"
                        >
                          <Edit className="h-4 w-4 md:h-5 md:w-5" />
                        </button>
                        
                        {/* Elimina */}
                        <button
                          onClick={() => handleDelete(config.id)}
                          className="p-1.5 md:p-2 rounded-lg transition-colors"
                          style={{ 
                            color: COLORS.error,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#FEE2E2')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4 md:h-5 md:w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Jobs Attivi */}
        {!isGuestUser && activeJobs.length > 0 && (
          <div 
            className="mt-8 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.05)] overflow-hidden"
            style={{ 
              backgroundColor: '#FFFFFF',
              border: `1px solid ${COLORS.border}`
            }}
          >
            <div className="p-6">
              <h2 
                className="text-xl font-semibold mb-4"
                style={{ color: COLORS.text }}
              >
                🟢 Jobs Attivi nello Scheduler ({activeJobs.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y" style={{ borderColor: COLORS.border }}>
                  <thead style={{ backgroundColor: COLORS.bg }}>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: COLORS.text2 }}>
                        Job ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: COLORS.text2 }}>
                        Nome
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: COLORS.text2 }}>
                        Prossima Esecuzione
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: COLORS.border }}>
                    {activeJobs.map((job) => (
                      <tr key={job.job_id}>
                        <td className="px-4 py-3 text-sm font-mono" style={{ color: COLORS.text2 }}>
                          {job.job_id}
                        </td>
                        <td className="px-4 py-3 text-sm" style={{ color: COLORS.text }}>
                          {job.name}
                        </td>
                        <td className="px-4 py-3 text-sm" style={{ color: COLORS.text2 }}>
                          {job.next_run_time ? formatNextRun(job.next_run_time) : 'N/D'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Guida */}
        {!isGuestUser && (
          <div 
            className="mt-8 rounded-xl border p-6"
            style={{ 
              backgroundColor: COLORS.bg,
              borderColor: COLORS.border
            }}
          >
            <h3 className="text-sm font-semibold mb-3" style={{ color: COLORS.text }}>
              📖 Guida Rapida
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" style={{ color: COLORS.text2 }}>
              <div>
                <p className="font-medium mb-1" style={{ color: COLORS.text }}>🔍 Filtri</p>
                <p>Configura regioni, province, categorie CPV e SOA per ricevere solo le gare di tuo interesse.</p>
              </div>
              <div>
                <p className="font-medium mb-1" style={{ color: COLORS.text }}>📅 Frequenza</p>
                <p>Scegli quando ricevere le notifiche: giornalmente, settimanalmente o con una frequenza personalizzata.</p>
              </div>
              <div>
                <p className="font-medium mb-1" style={{ color: COLORS.text }}>⚡ Esegui Ora</p>
                <p>Testa la tua configurazione inviando immediatamente una email con le gare corrispondenti.</p>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default SchedulerPage;
